export type FieldType = 'text' | 'number' | 'date' | 'enum' | 'boolean' | 'lookup';

export type Operator =
    | 'eq' | 'neq' | 'contains' | 'starts_with' | 'ends_with'
    | 'gt' | 'lt' | 'gte' | 'lte' | 'between'
    | 'before' | 'after' | 'between_dates'
    | 'is'
    | 'in' | 'not_in'
    | 'is_true' | 'is_false' | 'is_unset'
    | 'in_lookup';

/**
 * O "não filtrar" de um campo BOOLEANO.
 *
 * ⚠️ Um booleano não tem valor: quem responde "sim ou não" é o OPERADOR. Sem este terceiro estado,
 * a linha existir no painel já significava filtrar — e como o painel repõe os campos visíveis a
 * cada busca (ver `panelRows`), remover "Em cobrança" e pesquisar de novo o trazia de volta
 * **valendo Sim**, silenciosamente. Texto e data não têm o problema: neles o vazio é o `value`.
 */
export const BOOLEAN_UNSET: Operator = 'is_unset';

export const operatorLabels: Record<Operator, string> = {
    eq: 'igual a',
    neq: 'diferente de',
    contains: 'contém',
    starts_with: 'começa com',
    ends_with: 'termina com',
    gt: 'maior que',
    lt: 'menor que',
    gte: 'maior ou igual',
    lte: 'menor ou igual',
    between: 'entre',
    before: 'antes de',
    after: 'depois de',
    between_dates: 'entre datas',
    is: 'é',
    in: 'é um de',
    not_in: 'não é',
    is_true: 'sim',
    is_false: 'não',
    is_unset: '—',
    in_lookup: 'pertence a',
};

export const operatorsByType: Record<FieldType, Operator[]> = {
    text: ['eq', 'neq', 'contains', 'starts_with', 'ends_with'],
    number: ['eq', 'neq', 'is', 'gt', 'lt', 'gte', 'lte', 'between'],
    date: ['is', 'before', 'after', 'between_dates'],
    enum: ['in', 'not_in'],
    // o `is_unset` vem primeiro: é o estado em que a linha nasce quando ninguém a pediu
    boolean: ['is_unset', 'is_true', 'is_false'],
    lookup: ['in_lookup'],
};

export interface FilterOption {
    value: string;
    label: string;
}

export interface LookupConfig {
    endpoint: string;
    searchField: string;
    displayField: string;
    valueField: string;
    schema?: FilterSchema[];
}

/**
 * Atalho que preenche operador e valor de um filtro em um clique.
 *
 * `apply` é uma FUNÇÃO, não um valor pronto: os presets de vencimento são relativos a HOJE, e um
 * valor congelado na montagem do schema (que é um módulo, avaliado uma vez) apontaria para o dia
 * em que a aba foi aberta — numa tela de cobrança deixada aberta a semana inteira, "vence hoje"
 * traria a segunda-feira passada.
 */
export interface FilterPreset {
    label: string;
    /** Dica opcional (title) — bom para explicar a janela que o atalho representa. */
    hint?: string;
    apply: () => { operator: Operator; value: any };
}

export interface FilterSchema {
    key: string;
    label: string;
    type: FieldType;
    operators: Operator[];
    defaultOperator: Operator;
    visible: boolean;
    options?: FilterOption[];
    lookupConfig?: LookupConfig;
    /** Atalhos exibidos ao lado do campo. */
    presets?: FilterPreset[];
    /**
     * Valor em reais: o campo vira `CurrencyInput` e o resumo mostra "R$ 1.234,56". O `value`
     * continua texto com ponto decimal ("1234.56"), que é o que a URL leva e o que as lupas semeiam.
     */
    currency?: boolean;
    /**
     * Número de documento (CPF/CNPJ): a busca vai sem espaços, ".", "-" e "/", e o campo continua
     * mostrando o que o usuário digitou. As letras ficam, por causa do CNPJ alfanumérico.
     */
    document?: boolean;
}

export interface AppliedFilter {
    id: string;
    key: string;
    label: string;
    type: FieldType;
    operator: Operator;
    value: any;
}

export interface FilterHistory {
    filters: AppliedFilter[];
    timestamp: number;
}
